import React, { useMemo } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  GiftIcon,
} from '@heroicons/react/24/outline';
import Button from '../ui/Button';

interface Tier {
  id: string;
  name: string;
  description: string;
  amount: number;
  currency: string;
  billingCycle: 'MONTHLY' | 'YEARLY';
  benefits: string[];
  isPublic: boolean;
  currentPatrons: number;
  maxPatrons?: number;
  customButtonText?: string;
  accessUrl?: string;
  sortOrder: number;
  isActive: boolean;
  freeTrialDays?: number;
}

interface TierComparisonProps {
  tiers: Tier[];
  onSelectTier: (tier: Tier) => void;
  className?: string;
}

/**
 * Tier Comparison Component
 * Shows all membership tiers side-by-side in a comparison table
 */
const TierComparison: React.FC<TierComparisonProps> = ({
  tiers,
  onSelectTier,
  className = '',
}) => {
  // Sort tiers by price (lowest to highest)
  const sortedTiers = useMemo(() => {
    return [...tiers].sort((a, b) => {
      // Normalize to monthly for comparison
      const aMonthly = a.billingCycle === 'YEARLY' ? a.amount / 12 : a.amount;
      const bMonthly = b.billingCycle === 'YEARLY' ? b.amount / 12 : b.amount;
      return aMonthly - bMonthly;
    });
  }, [tiers]);

  // Extract all unique benefits across all tiers
  const allBenefits = useMemo(() => {
    const benefitsSet = new Set<string>();
    tiers.forEach((tier) => {
      tier.benefits.forEach((benefit) => benefitsSet.add(benefit));
    });
    return Array.from(benefitsSet);
  }, [tiers]);

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount / 100);
  };

  const isTierFull = (tier: Tier): boolean => {
    return !!(tier.maxPatrons && tier.currentPatrons >= tier.maxPatrons);
  };

  if (sortedTiers.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <CurrencyDollarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No tiers available</h3>
        <p className="text-gray-600">This creator hasn't set up any membership tiers yet.</p>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      {/* Mobile View - Cards */}
      <div className="block lg:hidden space-y-6">
        {sortedTiers.map((tier) => (
          <div
            key={tier.id}
            className="bg-white border-2 border-gray-200 rounded-lg p-6 relative"
          >
            {isTierFull(tier) && (
              <div className="absolute top-4 right-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                  Sold Out
                </span>
              </div>
            )}

            <div className="mb-4">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
              <p className="text-sm text-gray-600 mb-4">{tier.description}</p>
              
              <div className="flex items-baseline mb-2">
                <span className="text-4xl font-bold text-gray-900">
                  {formatCurrency(tier.amount, tier.currency)}
                </span>
                <span className="ml-2 text-gray-600">
                  / {tier.billingCycle === 'MONTHLY' ? 'month' : 'year'}
                </span>
              </div>

              {tier.freeTrialDays && tier.freeTrialDays > 0 && (
                <div className="flex items-center gap-1 text-green-600 text-sm mb-2">
                  <GiftIcon className="w-4 h-4" />
                  <span>{tier.freeTrialDays}-day free trial</span>
                </div>
              )}

              <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                <div className="flex items-center gap-1">
                  <UserGroupIcon className="w-4 h-4" />
                  <span>{tier.currentPatrons} patrons</span>
                </div>
                {tier.maxPatrons && (
                  <span>
                    {tier.maxPatrons - tier.currentPatrons} spots left
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <h4 className="font-semibold text-gray-900">Benefits:</h4>
              {tier.benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>

            <Button
              className="w-full"
              onClick={() => onSelectTier(tier)}
              disabled={isTierFull(tier)}
            >
              {isTierFull(tier)
                ? 'Sold Out'
                : tier.customButtonText || `Choose ${tier.name}`}
            </Button>
          </div>
        ))}
      </div>

      {/* Desktop View - Comparison Table */}
      <div className="hidden lg:block overflow-x-auto">
        <div className="inline-block min-w-full align-middle">
          <div className="grid gap-4" style={{ gridTemplateColumns: `200px repeat(${sortedTiers.length}, 1fr)` }}>
            {/* Header Row */}
            <div className="h-12"></div>
            {sortedTiers.map((tier) => (
              <div
                key={`header-${tier.id}`}
                className="bg-gradient-to-b from-gray-50 to-white border-2 border-gray-200 rounded-t-lg p-6 relative"
              >
                {isTierFull(tier) && (
                  <div className="absolute top-2 right-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                      Sold Out
                    </span>
                  </div>
                )}
                <h3 className="text-xl font-bold text-gray-900 mb-3">{tier.name}</h3>
                <p className="text-sm text-gray-600 mb-4 min-h-[40px]">{tier.description}</p>
                
                <div className="mb-4">
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-gray-900">
                      {formatCurrency(tier.amount, tier.currency)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    per {tier.billingCycle === 'MONTHLY' ? 'month' : 'year'}
                  </p>
                </div>

                <Button
                  className="w-full"
                  onClick={() => onSelectTier(tier)}
                  disabled={isTierFull(tier)}
                  size="sm"
                >
                  {isTierFull(tier)
                    ? 'Sold Out'
                    : tier.customButtonText || 'Select'}
                </Button>
              </div>
            ))}

            {/* Free Trial Row */}
            <div className="bg-gray-50 p-4 flex items-center border-l-2 border-gray-200 font-semibold text-sm text-gray-700">
              <GiftIcon className="w-5 h-5 mr-2 text-gray-600" />
              Free Trial
            </div>
            {sortedTiers.map((tier) => (
              <div
                key={`trial-${tier.id}`}
                className="bg-white border-x-2 border-gray-200 p-4 flex items-center justify-center"
              >
                {tier.freeTrialDays && tier.freeTrialDays > 0 ? (
                  <span className="text-green-600 font-semibold text-sm flex items-center gap-1">
                    <CheckCircleIcon className="w-5 h-5" />
                    {tier.freeTrialDays} days
                  </span>
                ) : (
                  <XCircleIcon className="w-5 h-5 text-gray-300" />
                )}
              </div>
            ))}

            {/* Patrons Count Row */}
            <div className="bg-gray-50 p-4 flex items-center border-l-2 border-gray-200 font-semibold text-sm text-gray-700">
              <UserGroupIcon className="w-5 h-5 mr-2 text-gray-600" />
              Current Patrons
            </div>
            {sortedTiers.map((tier) => (
              <div
                key={`patrons-${tier.id}`}
                className="bg-white border-x-2 border-gray-200 p-4 flex flex-col items-center justify-center"
              >
                <span className="font-semibold text-gray-900">{tier.currentPatrons}</span>
                {tier.maxPatrons && (
                  <span className="text-xs text-gray-500 mt-1">
                    of {tier.maxPatrons}
                  </span>
                )}
              </div>
            ))}

            {/* Benefits Section Header */}
            <div className="bg-gray-50 p-4 border-l-2 border-gray-200 font-semibold text-sm text-gray-900 col-span-full">
              Benefits Included
            </div>

            {/* Benefits Rows */}
            {allBenefits.map((benefit, idx) => (
              <React.Fragment key={`benefit-${idx}`}>
                <div className="bg-gray-50 p-4 flex items-center border-l-2 border-gray-200 text-sm text-gray-700">
                  {benefit}
                </div>
                {sortedTiers.map((tier) => (
                  <div
                    key={`benefit-${tier.id}-${idx}`}
                    className="bg-white border-x-2 border-gray-200 p-4 flex items-center justify-center"
                  >
                    {tier.benefits.includes(benefit) ? (
                      <CheckCircleIcon className="w-6 h-6 text-green-500" />
                    ) : (
                      <XCircleIcon className="w-6 h-6 text-gray-300" />
                    )}
                  </div>
                ))}
              </React.Fragment>
            ))}

            {/* Bottom Border */}
            <div className="h-0"></div>
            {sortedTiers.map((tier) => (
              <div
                key={`bottom-${tier.id}`}
                className="border-b-2 border-x-2 border-gray-200 rounded-b-lg"
              />
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-8 flex items-center justify-center gap-6 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <CheckCircleIcon className="w-5 h-5 text-green-500" />
          <span>Included</span>
        </div>
        <div className="flex items-center gap-2">
          <XCircleIcon className="w-5 h-5 text-gray-300" />
          <span>Not included</span>
        </div>
      </div>
    </div>
  );
};

export default TierComparison;
